// Journal Entries service - moved from database.js (comprehensive accounting logic)
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  serverTimestamp, 
  db 
} from '../../utils/database/firebase-config';

// Note: These imports will be updated once all services are moved
// import { invoicesService } from './invoices';
// import { paymentTrackingService } from '../financial/payment-tracking';
// import { accountMappingService } from './account-mapping';
// import { chartOfAccountsService } from './chart-of-accounts';

export const journalEntriesService = {
  async getJournalEntries(companyId) {
    const entriesRef = collection(db, 'journalEntries');
    const q = query(entriesRef, where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Add this function to manually create journal entries for existing invoices/payments
  async createMissingJournalEntries(companyId, createdBy) {
    console.log('🔄 Creating missing journal entries for company:', companyId);
    
    try {
      // Get all invoices and existing journal entries
      // const invoices = await invoicesService.getInvoices(companyId);
      const invoices = [];
      const existingJournalEntries = await this.getJournalEntries(companyId);
      
      console.log(`📋 Found ${invoices.length} total invoices`);
      console.log(`📋 Found ${existingJournalEntries.length} existing journal entries`);
      
      // Find invoices that don't have journal entries (improved duplicate detection)
      const invoicesWithoutJournals = invoices.filter(invoice => {
        const hasJournalEntry = existingJournalEntries.some(entry => {
          return entry.sourceType === 'invoice' && (
            entry.sourceId === invoice.docId || // Check by Firestore document ID
            entry.sourceId === invoice.id ||    // Check by invoice ID (could be invoice number)
            entry.reference === invoice.id ||   // Check by reference (could be invoice number)
            entry.reference === invoice.docId ||// Check by reference (could be document ID)
            (entry.description && entry.description.includes(invoice.id)) // Check description
          );
        });
        return !hasJournalEntry;
      });
      
      console.log(`📋 Found ${invoicesWithoutJournals.length} invoices without journal entries`);
      
      // Create journal entries for invoices
      let successfulInvoiceEntries = 0;
      for (const invoice of invoicesWithoutJournals) {
        try {
          console.log(`🔄 Creating journal entry for invoice: ${invoice.id} (docId: ${invoice.docId})`);
          
          // Use the proper invoice structure for journal entry creation
          const invoiceForJournal = {
            ...invoice,
            id: invoice.docId || invoice.id, // Use Firestore document ID as primary
            invoiceNumber: invoice.id,        // Invoice number for display
            invoiceDate: invoice.issueDate,
            finalTotal: invoice.finalTotal || invoice.total || invoice.totalAmount,
            client: invoice.client || { name: invoice.clientName || 'Unknown Client' }
          };
          
          await this.createRevenueJournalEntry(companyId, invoiceForJournal, createdBy);
          console.log(`✅ Created journal entry for invoice ${invoice.id}`);
          successfulInvoiceEntries++;
          
        } catch (error) {
          console.error(`❌ Failed to create journal entry for invoice ${invoice.id}:`, error);
        }
      }
      
      // Get all payments without journal entries (improved duplicate detection)
      // const payments = await paymentTrackingService.getPayments(companyId);
      const payments = [];
      const paymentsWithoutJournals = payments.filter(payment => {
        const hasJournalEntry = existingJournalEntries.some(entry => {
          return entry.sourceType === 'payment' && (
            entry.sourceId === payment.id ||
            entry.reference === payment.id ||
            (entry.description && entry.description.includes(payment.id))
          );
        });
        return !hasJournalEntry;
      });
      
      console.log(`📋 Found ${paymentsWithoutJournals.length} payments without journal entries`);
      
      // Create journal entries for payments
      let successfulPaymentEntries = 0;
      for (const payment of paymentsWithoutJournals) {
        try {
          // await paymentTrackingService.createPaymentJournalEntry(companyId, payment, createdBy);
          console.log(`✅ Created journal entry for payment ${payment.id}`);
          successfulPaymentEntries++;
        } catch (error) {
          console.error(`❌ Failed to create journal entry for payment ${payment.id}:`, error);
        }
      }
      
      console.log(`✅ Successfully created ${successfulInvoiceEntries} invoice entries and ${successfulPaymentEntries} payment entries`);
      
      return {
        invoicesProcessed: successfulInvoiceEntries,
        paymentsProcessed: successfulPaymentEntries,
        invoicesFound: invoicesWithoutJournals.length,
        paymentsFound: paymentsWithoutJournals.length
      };
      
    } catch (error) {
      console.error('❌ Error creating missing journal entries:', error);
      throw error;
    }
  },
  async getJournalEntry(entryId) {
    const entryRef = doc(db, 'journalEntries', entryId);
    const snapshot = await getDoc(entryRef);
    if (snapshot.exists()) return { id: snapshot.id, ...snapshot.data() };
    return null;
  },
  async addJournalEntry(entryData) {
    try {
      // Validate lineItems exists
      if (!entryData.lineItems || !Array.isArray(entryData.lineItems)) {
        throw new Error('Journal entry must have valid lineItems array');
      }
      
      // Validate double-entry (debits = credits)
      const totalDebits = entryData.lineItems.reduce((sum, item) => sum + (parseFloat(item.debit) || 0), 0);
      const totalCredits = entryData.lineItems.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0);
      
      if (Math.abs(totalDebits - totalCredits) > 0.01) {
        throw new Error('Journal entry does not balance: debits must equal credits');
      }

      // Generate entry number if not provided
      if (!entryData.entryNumber) {
        entryData.entryNumber = await this.generateEntryNumber(entryData.companyId);
      }

      const entriesRef = collection(db, 'journalEntries');
      const entryWithTotals = {
        ...entryData,
        totalDebit: totalDebits,
        totalCredit: totalCredits,
        status: entryData.status || 'draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('Creating journal entry:', entryWithTotals);
      const docRef = await addDoc(entriesRef, entryWithTotals);
      console.log('Journal entry created successfully:', docRef.id);
      return docRef.id;
      
    } catch (error) {
      console.error('Error in addJournalEntry:', error);
      
      // Provide more specific error messages
      if (error.code === 'failed-precondition') {
        throw new Error('Database index required. Please contact support or try again later.');
      } else if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your account permissions.');
      } else if (error.code === 'unavailable') {
        throw new Error('Database temporarily unavailable. Please try again.');
      } else {
        throw new Error(`Failed to create journal entry: ${error.message}`);
      }
    }
  },
  async updateJournalEntry(entryId, entryData) {
    // Get existing entry to check if it can be edited
    const existingEntry = await this.getJournalEntry(entryId);
    if (!existingEntry) throw new Error('Journal entry not found');
    
    if (existingEntry.status === 'posted') {
      throw new Error('Cannot edit posted journal entries. Please reverse and create a new entry.');
    }

    // Validate double-entry if line items are being updated
    if (entryData.lineItems) {
      if (!Array.isArray(entryData.lineItems)) {
        throw new Error('lineItems must be an array');
      }
      
      const totalDebits = entryData.lineItems.reduce((sum, item) => sum + (parseFloat(item.debit) || 0), 0);
      const totalCredits = entryData.lineItems.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0);
      
      if (Math.abs(totalDebits - totalCredits) > 0.01) {
        throw new Error('Journal entry does not balance: debits must equal credits');
      }
      
      entryData.totalDebit = totalDebits;
      entryData.totalCredit = totalCredits;
    }

    const entryRef = doc(db, 'journalEntries', entryId);
    await updateDoc(entryRef, { ...entryData, updatedAt: serverTimestamp() });
    return true;
  },
  async deleteJournalEntry(entryId) {
    // Get existing entry to check if it can be deleted
    const existingEntry = await this.getJournalEntry(entryId);
    if (!existingEntry) throw new Error('Journal entry not found');
    
    if (existingEntry.status === 'posted') {
      throw new Error('Cannot delete posted journal entries. Please reverse the entry instead.');
    }

    const entryRef = doc(db, 'journalEntries', entryId);
    await deleteDoc(entryRef);
    return true;
  },
  async postJournalEntry(entryId, postedBy) {
    const existingEntry = await this.getJournalEntry(entryId);
    if (!existingEntry) throw new Error('Journal entry not found');
    
    if (existingEntry.status !== 'draft') {
      throw new Error('Only draft entries can be posted');
    }

    // Update account balances
    for (const lineItem of existingEntry.lineItems) {
      if (lineItem.debit > 0) {
        // await chartOfAccountsService.updateAccountBalance(lineItem.accountId, lineItem.debit, 'debit');
      }
      if (lineItem.credit > 0) {
        // await chartOfAccountsService.updateAccountBalance(lineItem.accountId, lineItem.credit, 'credit');
      }
    }

    const entryRef = doc(db, 'journalEntries', entryId);
    await updateDoc(entryRef, { 
      status: 'posted',
      postedAt: serverTimestamp(),
      postedBy,
      updatedAt: serverTimestamp()
    });
    return true;
  },
  async reverseJournalEntry(entryId, reversedBy, reason) {
    // Get original entry
    const originalEntry = await this.getJournalEntry(entryId);
    if (!originalEntry) throw new Error('Journal entry not found');
    
    if (originalEntry.status !== 'posted') {
      throw new Error('Only posted entries can be reversed');
    }
    
    // Validate original entry has lineItems
    if (!originalEntry.lineItems || !Array.isArray(originalEntry.lineItems)) {
      throw new Error('Original journal entry has invalid lineItems');
    }
    
    // Create reversal entry
    const reversalLineItems = originalEntry.lineItems.map(item => ({
      ...item,
      debit: item.credit || 0,
      credit: item.debit || 0,
      description: `Reversal: ${item.description}`
    }));
    
    const reversalEntry = {
      companyId: originalEntry.companyId,
      entryNumber: await this.generateEntryNumber(originalEntry.companyId),
      date: new Date().toISOString().split('T')[0],
      entryType: 'reversal',
      description: `Reversal of ${originalEntry.entryNumber}: ${reason}`,
      reference: originalEntry.reference,
      lineItems: reversalLineItems,
      status: 'draft',
      createdBy: reversedBy,
      reversalOf: originalEntry.id,
      autoGenerated: true
    };
    
    // Create reversal entry
    const reversalId = await this.addJournalEntry(reversalEntry);
    
    // Auto-post the reversal entry
    await this.postJournalEntry(reversalId, reversedBy);
    
    // Mark original as reversed
    await updateDoc(doc(db, 'journalEntries', entryId), {
      status: 'reversed',
      reversedAt: serverTimestamp(),
      reversedBy,
      reversalEntryId: reversalId,
      updatedAt: serverTimestamp()
    });
    
    return reversalId;
  },
  async generateEntryNumber(companyId) {
    try {
      // Generate shorter entry number with date integration
      const today = new Date();
      const year = today.getFullYear().toString().slice(-2); // Last 2 digits of year
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      
      // Use simpler query to avoid index issues - just get all entries for company
      const entriesRef = collection(db, 'journalEntries');
      const q = query(entriesRef, where('companyId', '==', companyId));
      
      const snapshot = await getDocs(q);
      
      // Filter entries for today in memory to avoid complex query
      const todayEntries = snapshot.docs.filter(doc => {
        const data = doc.data();
        if (!data.createdAt) return false;
        
        const entryDate = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
        const entryDateStr = entryDate.toDateString();
        const todayStr = today.toDateString();
        
        return entryDateStr === todayStr;
      });
      
      const count = todayEntries.length + 1;
      
      return `J${year}${month}${day}-${String(count).padStart(2, '0')}`;
    } catch (error) {
      console.error('Error generating entry number:', error);
      // Fallback to timestamp-based number
      const timestamp = Date.now().toString().slice(-6);
      return `J${timestamp}`;
    }
  },
  
  // ==================== AUTOMATIC JOURNAL ENTRY CREATION ====================
  async createRevenueJournalEntry(companyId, invoice, createdBy) {
    // Get configurable account mappings
    // const accountMappings = await accountMappingService.getAccountMappings(companyId);
    const accountMappings = {
      assets: { accountsReceivable: '1100' },
      revenue: { logisticsRevenue: '4000' },
      liabilities: { vatPayable: '2100' }
    };
    
    const lineItems = [
      {
        accountCode: accountMappings.assets.accountsReceivable,
        accountName: 'Accounts Receivable',
        accountId: await this.getAccountIdByCode(companyId, accountMappings.assets.accountsReceivable),
        debit: invoice.finalTotal,
        credit: 0,
        description: `Invoice ${invoice.invoiceNumber} - ${invoice.client?.name || 'Unknown Client'}`
      },
      {
        accountCode: accountMappings.revenue.logisticsRevenue,
        accountName: 'Logistics Service Revenue',
        accountId: await this.getAccountIdByCode(companyId, accountMappings.revenue.logisticsRevenue),
        debit: 0,
        credit: invoice.subtotal,
        description: `Revenue for invoice ${invoice.invoiceNumber}`
      }
    ];

    // Add VAT line item if VAT exists
    if (invoice.vatAmount > 0) {
      lineItems.push({
        accountCode: accountMappings.liabilities.vatPayable,
        accountName: 'VAT Payable',
        accountId: await this.getAccountIdByCode(companyId, accountMappings.liabilities.vatPayable),
        debit: 0,
        credit: invoice.vatAmount,
        description: `VAT for invoice ${invoice.invoiceNumber}`
      });
    }

    const entryData = {
      companyId,
      entryNumber: await this.generateEntryNumber(companyId),
      date: invoice.invoiceDate,
      entryType: 'automatic',
      description: `Revenue recognition for invoice ${invoice.invoiceNumber}`,
      reference: invoice.id,
      sourceType: 'invoice',
      sourceId: invoice.id,
      lineItems,
      status: 'posted',
      createdBy,
      autoGenerated: true
    };

    const journalId = await this.addJournalEntry(entryData);
    // Auto-post since it's an automatic entry
    await this.postJournalEntry(journalId, createdBy);
    return journalId;
  },
  
  async createExpenseJournalEntry(companyId, expense, createdBy) {
    // Get configurable account mappings
    // const accountMappings = await accountMappingService.getAccountMappings(companyId);
    const accountMappings = {
      expenses: { fuel: '5000', maintenance: '5100', salary: '5200', office: '5300', marketing: '5400', insurance: '5500', commission: '5700', utilities: '5800', other: '5900' },
      assets: { cash: '1000', vatReceivable: '1300' }
    };
    
    // Map expense categories to account codes using configurable mappings
    const expenseAccountMap = {
      'fuel': accountMappings.expenses.fuel,
      'maintenance': accountMappings.expenses.maintenance,
      'salary': accountMappings.expenses.salary,
      'office': accountMappings.expenses.office,
      'marketing': accountMappings.expenses.marketing,
      'insurance': accountMappings.expenses.insurance,
      'commission': accountMappings.expenses.commission,
      'utilities': accountMappings.expenses.utilities,
      'other': accountMappings.expenses.other
    };

    const accountCode = expenseAccountMap[expense.category] || accountMappings.expenses.other;
    const accountId = await this.getAccountIdByCode(companyId, accountCode);
    const cashAccountId = await this.getAccountIdByCode(companyId, accountMappings.assets.cash);
    
    // Validate VAT calculation to prevent double counting
    const baseAmount = expense.baseAmount || expense.amount || 0;
    const vatAmount = expense.vatAmount || 0;
    const totalAmount = expense.totalAmount || expense.amount || 0;
    
    // Validation: ensure baseAmount + vatAmount equals totalAmount
    if (Math.abs((baseAmount + vatAmount) - totalAmount) > 0.01) {
      throw new Error(`VAT calculation validation failed for expense ${expense.id}: baseAmount (${baseAmount}) + vatAmount (${vatAmount}) != totalAmount (${totalAmount})`);
    }
    
    const lineItems = [
      {
        accountCode,
        accountName: expense.category.charAt(0).toUpperCase() + expense.category.slice(1) + ' Expenses',
        accountId,
        debit: baseAmount, // Use base amount (without VAT)
        credit: 0,
        description: expense.description
      },
      {
        accountCode: accountMappings.assets.cash,
        accountName: 'Cash',
        accountId: cashAccountId,
        debit: 0,
        credit: totalAmount, // Total payment amount
        description: `Payment for ${expense.description}`
      }
    ];

    // Add VAT receivable if VAT exists (and it's not fuel or salary which are VAT-exempt)
    if (vatAmount > 0 && !['fuel', 'salary'].includes(expense.category)) {
      const vatAccountId = await this.getAccountIdByCode(companyId, accountMappings.assets.vatReceivable);
      lineItems.push({
        accountCode: accountMappings.assets.vatReceivable,
        accountName: 'VAT Receivable',
        accountId: vatAccountId,
        debit: vatAmount,
        credit: 0,
        description: `VAT on ${expense.description}`
      });
    }

    const entryData = {
      companyId,
      entryNumber: await this.generateEntryNumber(companyId),
      date: expense.date,
      entryType: 'automatic',
      description: `Expense entry for ${expense.description}`,
      reference: expense.id,
      sourceType: 'expense',
      sourceId: expense.id,
      lineItems,
      status: 'posted',
      createdBy,
      autoGenerated: true,
      metadata: {
        vatBreakdown: {
          baseAmount,
          vatAmount,
          totalAmount,
          vatIncluded: expense.vatIncluded,
          calculationMethod: expense.calculationMethod
        }
      }
    };

    const journalId = await this.addJournalEntry(entryData);
    // Auto-post since it's an automatic entry
    await this.postJournalEntry(journalId, createdBy);
    return journalId;
  },
  
  async getAccountIdByCode(companyId, accountCode) {
    try {
      const accountsRef = collection(db, 'chartOfAccounts');
      const q = query(accountsRef, where('companyId', '==', companyId), where('accountCode', '==', accountCode));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.error(`Account with code ${accountCode} not found for company ${companyId}`);
        // Try to get all accounts for debugging
        const allAccountsRef = collection(db, 'chartOfAccounts');
        const allQ = query(allAccountsRef, where('companyId', '==', companyId));
        const allSnapshot = await getDocs(allQ);
        const allAccounts = allSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Available accounts:', allAccounts.map(acc => ({ code: acc.accountCode, name: acc.accountName })));
        throw new Error(`Account with code ${accountCode} not found. Available accounts: ${allAccounts.map(acc => acc.accountCode).join(', ')}`);
      }
      
      return snapshot.docs[0].id;
    } catch (error) {
      console.error('Error in getAccountIdByCode:', error);
      throw error;
    }
  },

  async createShipmentCostingJournalEntry(companyId, shipment, costingData, createdBy, automationSettings = null) {
    try {
      console.log('🔄 Creating shipment costing journal entry for:', shipment.id);
      console.log('🔄 Automation settings received:', automationSettings);
      
      // Check for existing journal entry to prevent duplicates
      const existingEntries = await this.checkForExistingJournalEntry(
        companyId, 
        'shipment_costing', 
        shipment.id
      );
      
      if (existingEntries.length > 0) {
        console.log('Journal entry already exists for this shipment costing, updating instead...');
        // Delete existing entry and create new one with updated data
        for (const entry of existingEntries) {
          await this.deleteJournalEntry(entry.id);
        }
      }
      
      // Check if chart of accounts is initialized
      // const accounts = await chartOfAccountsService.getChartOfAccounts(companyId);
      // if (accounts.length === 0) {
      //   console.log('Chart of accounts not found, initializing default accounts...');
      //   await chartOfAccountsService.initializeDefaultChartOfAccounts(companyId, createdBy);
      // }

      // Get configurable account mappings
      // const accountMappings = await accountMappingService.getAccountMappings(companyId);
      const accountMappings = {
        expenses: { fuel: '5000', maintenance: '5100', salary: '5200', commission: '5700', other: '5900' },
        assets: { cash: '1000', vatReceivable: '1300' }
      };
      console.log('📊 Using account mappings:', accountMappings);

      const lineItems = [];
      let totalAmount = 0;
      let totalVAT = 0;
      const costingBreakdown = [];

      // ✅ PROCESS EACH COST TYPE WITH AUTOMATION SETTINGS CHECK AND VAT BREAKDOWN
      const costTypes = [
        { 
          key: 'fuelCost', 
          type: 'fuel', 
          description: 'Fuel expenses', 
          accountCode: accountMappings.expenses.fuel,
          automationKey: 'fuelExpenses',
          enabled: !automationSettings || automationSettings.fuelExpenses?.journalEntryEnabled !== false,
          baseAmount: costingData.fuelBaseAmount || 0,
          vatAmount: costingData.fuelVATAmount || 0,
          includesVAT: costingData.fuelIncludesVAT,
          hasVAT: false // Fuel is VAT-exempt in Saudi Arabia
        },
        { 
          key: 'maintenanceCost', 
          type: 'maintenance', 
          description: 'Vehicle maintenance', 
          accountCode: accountMappings.expenses.maintenance,
          automationKey: 'maintenanceExpenses',
          enabled: !automationSettings || automationSettings.maintenanceExpenses?.journalEntryEnabled !== false,
          baseAmount: costingData.maintenanceBaseAmount || 0,
          vatAmount: costingData.maintenanceVATAmount || 0,
          includesVAT: costingData.maintenanceIncludesVAT,
          hasVAT: true // Maintenance is subject to VAT
        },
        { 
          key: 'driverSalary', 
          type: 'salary', 
          description: 'Driver salary', 
          accountCode: accountMappings.expenses.salary,
          automationKey: 'payrollExpenses',
          enabled: !automationSettings || automationSettings.payrollExpenses?.journalEntryEnabled !== false,
          baseAmount: costingData.driverSalary || 0,
          vatAmount: 0,
          includesVAT: false,
          hasVAT: false // Salary is VAT-exempt
        },
        { 
          key: 'commissionCost', 
          type: 'commission', 
          description: 'Commission expense', 
          accountCode: accountMappings.expenses.commission,
          automationKey: 'commissionExpenses',
          enabled: !automationSettings || automationSettings.commissionExpenses?.journalEntryEnabled !== false,
          baseAmount: costingData.commissionCost || 0,
          vatAmount: 0,
          includesVAT: false,
          hasVAT: false // Commission is VAT-exempt
        },
        { 
          key: 'otherCosts', 
          type: 'other', 
          description: 'Other expenses', 
          accountCode: accountMappings.expenses.other,
          automationKey: 'otherExpenses',
          enabled: true, // Other costs are always enabled
          baseAmount: costingData.otherCosts || 0,
          vatAmount: 0,
          includesVAT: false,
          hasVAT: false // Other costs assumed VAT-exempt for now
        }
      ];

      for (const costType of costTypes) {
        const amount = costingData[costType.key];
        
        // ✅ CHECK AUTOMATION SETTINGS BEFORE CREATING LINE ITEMS
        if (amount && amount > 0 && costType.enabled) {
          console.log(`✅ Creating journal line for ${costType.type} (automation enabled):`, amount);
        } else if (amount && amount > 0 && !costType.enabled) {
          console.log(`⚠️ Skipping journal line for ${costType.type} (automation disabled):`, amount);
          continue;
        } else {
          continue; // Skip if amount is 0
        }
        
        if (amount && amount > 0) {
          
          // Add error handling for account lookup
          let accountId;
          try {
            accountId = await this.getAccountIdByCode(companyId, costType.accountCode);
          } catch (error) {
            console.error(`Failed to find account ${costType.accountCode} for ${costType.type}:`, error);
            throw new Error(`Account ${costType.accountCode} not found for ${costType.type} expenses. Please check chart of accounts or account mappings.`);
          }
          
          // ✅ USE VAT BREAKDOWN FROM COSTING DATA INSTEAD OF RECALCULATING
          const baseAmount = costType.baseAmount || amount; // Use calculated base amount
          const vatAmount = costType.hasVAT ? (costType.vatAmount || 0) : 0; // Use calculated VAT amount

          // Use fallback values for shipment properties
          const shipmentNumber = shipment.shipmentNumber || shipment.trackingNumber || shipment.id;
          
          lineItems.push({
            accountCode: costType.accountCode,
            accountName: `${costType.type.charAt(0).toUpperCase() + costType.type.slice(1)} Expenses`,
            accountId,
            debit: baseAmount, // Use base amount (without VAT)
            credit: 0,
            description: `${costType.description} for shipment ${shipmentNumber}`
          });

          totalAmount += baseAmount;
          if (costType.hasVAT && vatAmount > 0) {
            totalVAT += vatAmount;
          }

          costingBreakdown.push({
            type: costType.type,
            baseAmount: baseAmount,
            vatAmount: vatAmount,
            totalAmount: baseAmount + vatAmount,
            includesVAT: costType.includesVAT,
            calculationMethod: costType.includesVAT ? 'inclusive' : 'exclusive'
          });
        }
      }

      // Only proceed if there are line items
      if (lineItems.length === 0) {
        throw new Error('No costs to record - all amounts are zero');
      }

      // Add VAT receivable if there's VAT
      if (totalVAT > 0) {
        let vatAccountId;
        try {
          vatAccountId = await this.getAccountIdByCode(companyId, accountMappings.assets.vatReceivable);
        } catch (error) {
          console.error('Failed to find VAT Receivable account:', error);
          throw new Error(`VAT Receivable account (${accountMappings.assets.vatReceivable}) not found. Please check chart of accounts or account mappings.`);
        }
        
        lineItems.push({
          accountCode: accountMappings.assets.vatReceivable,
          accountName: 'VAT Receivable',
          accountId: vatAccountId,
          debit: totalVAT,
          credit: 0,
          description: `VAT on shipment ${shipment.shipmentNumber || shipment.trackingNumber || shipment.id} costs`
        });
      }

      // Add cash credit (total payment)
      let cashAccountId;
      try {
        cashAccountId = await this.getAccountIdByCode(companyId, accountMappings.assets.cash);
      } catch (error) {
        console.error('Failed to find Cash account:', error);
        throw new Error(`Cash account (${accountMappings.assets.cash}) not found. Please check chart of accounts or account mappings.`);
      }
      
      lineItems.push({
        accountCode: accountMappings.assets.cash,
        accountName: 'Cash',
        accountId: cashAccountId,
        debit: 0,
        credit: totalAmount + totalVAT, // Total payment includes base amount + VAT
        description: `Payment for shipment ${shipment.shipmentNumber || shipment.trackingNumber || shipment.id} costs`
      });

      // Create the comprehensive journal entry
      const shipmentNumber = shipment.shipmentNumber || shipment.trackingNumber || shipment.id;
      const fromLocation = shipment.fromLocation || shipment.origin || shipment.pickupLocation || 'Unknown';
      const toLocation = shipment.toLocation || shipment.destination || shipment.deliveryLocation || 'Unknown';
      
      const entryData = {
        companyId,
        entryNumber: await this.generateEntryNumber(companyId),
        date: costingData.date || new Date().toISOString().split('T')[0],
        entryType: 'automatic',
        description: `Shipment costing for ${shipmentNumber} - ${fromLocation} to ${toLocation}`,
        reference: shipment.id,
        sourceType: 'shipment_costing',
        sourceId: shipment.id,
        lineItems,
        status: 'draft',
        createdBy,
        autoGenerated: true,
        metadata: {
          shipmentNumber: shipmentNumber,
          costingBreakdown,
          totalAmount: totalAmount + totalVAT, // Total payment amount
          baseAmount: totalAmount, // Base amount without VAT
          totalVAT
        }
      };

      console.log('🔄 Creating journal entry with data:', entryData);
      const journalId = await this.addJournalEntry(entryData);
      console.log('✅ Journal entry created with ID:', journalId);
      
      // Auto-post since it's an automatic entry
      await this.postJournalEntry(journalId, createdBy);
      console.log('✅ Journal entry posted successfully');
      
      return journalId;
    } catch (error) {
      console.error('❌ Error creating shipment costing journal entry:', error);
      throw error;
    }
  },

  // ==================== FINANCIAL METRICS FROM JOURNAL ENTRIES ====================
  getDefaultFinancialMetrics() {
    return {
      revenue: 0,
      expenses: 0,
      profit: 0,
      cashFlow: 0,
      accountsReceivable: 0,
      accountsPayable: 0,
      vatPayable: 0,
      cash: 0,
      grossMargin: 0,
      revenueFromJournal: 0,
      expensesFromJournal: 0
    };
  },

  async calculateFinancialMetricsFromJournal(companyId) {
    try {
      console.log('🔄 Starting calculateFinancialMetricsFromJournal for company:', companyId);
      
      // Get all posted journal entries
      const journalEntries = await this.getJournalEntries(companyId);
      console.log('📋 Retrieved journal entries:', journalEntries.length);
      
      // Ensure journalEntries is an array
      if (!Array.isArray(journalEntries)) {
        console.warn('⚠️ journalEntries is not an array:', journalEntries);
        return this.getDefaultFinancialMetrics();
      }
      
      const postedEntries = journalEntries.filter(entry => entry && entry.status === 'posted');
      console.log('📋 Posted entries:', postedEntries.length);

      // If no posted entries, return default metrics
      if (!postedEntries || postedEntries.length === 0) {
        console.log('ℹ️ No posted journal entries found, returning default metrics');
        return this.getDefaultFinancialMetrics();
      }

      // Calculate metrics from journal entries only
      let totalRevenue = 0;
      let totalExpenses = 0;
      let accountsReceivable = 0;
      let accountsPayable = 0;
      let vatPayable = 0;
      let vatReceivable = 0;
      let cash = 0;

      // Process each posted journal entry
      postedEntries.forEach((entry, index) => {
        try {
          console.log(`🔍 Processing entry ${index + 1}/${postedEntries.length}:`, entry.id);
          
          // Skip entries without lineItems
          if (!entry.lineItems || !Array.isArray(entry.lineItems)) {
            console.warn('⚠️ Journal entry missing lineItems:', entry.id, 'lineItems:', entry.lineItems);
            return;
          }
          
          console.log(`📝 Entry has ${entry.lineItems.length} line items`);
          
          entry.lineItems.forEach((lineItem, lineIndex) => {
            try {
              console.log(`  📄 Processing line item ${lineIndex + 1}:`, lineItem.accountCode);
              const accountCode = lineItem.accountCode;
              const debit = parseFloat(lineItem.debit) || 0;
              const credit = parseFloat(lineItem.credit) || 0;

              // Revenue accounts (4000-4999)
              if (accountCode.startsWith('4')) {
                totalRevenue += credit - debit; // Revenue increases with credits
              }
              
              // Expense accounts (5000-5999)
              else if (accountCode.startsWith('5')) {
                totalExpenses += debit - credit; // Expenses increase with debits
              }
              
              // Specific account balances
              else if (accountCode === '1100') { // Accounts Receivable
                accountsReceivable += debit - credit;
              }
              else if (accountCode === '2000') { // Accounts Payable
                accountsPayable += credit - debit;
              }
              else if (accountCode === '2100') { // VAT Payable
                vatPayable += credit - debit;
              }
              else if (accountCode === '1300') { // VAT Receivable
                vatReceivable += debit - credit;
              }
              else if (accountCode === '1000') { // Cash
                cash += debit - credit;
              }
            } catch (lineItemError) {
              console.error('❌ Error processing line item:', lineItemError, 'LineItem:', lineItem);
            }
          });
        } catch (entryError) {
          console.error('❌ Error processing journal entry:', entryError, 'Entry:', entry);
        }
      });

      // Calculate derived metrics
      const netProfit = totalRevenue - totalExpenses;
      const netVAT = vatPayable - vatReceivable;

      console.log('✅ Financial metrics calculated successfully:', {
        revenue: totalRevenue,
        expenses: totalExpenses,
        profit: netProfit
      });

      return {
        revenue: totalRevenue,
        expenses: totalExpenses,
        profit: netProfit,
        cashFlow: netProfit, // Simplified - would need cash flow statement for accuracy
        accountsReceivable,
        accountsPayable,
        vatPayable: netVAT,
        cash,
        // Additional metrics
        grossMargin: totalRevenue > 0 ? ((totalRevenue - totalExpenses) / totalRevenue) * 100 : 0,
        revenueFromJournal: totalRevenue,
        expensesFromJournal: totalExpenses
      };
    } catch (error) {
      console.error('Error calculating financial metrics from journal:', error);
      console.warn('⚠️ Returning default financial metrics due to error');
      return this.getDefaultFinancialMetrics();
    }
  },

  // ==================== DOUBLE-ENTRY PREVENTION ====================
  async checkForExistingJournalEntry(companyId, sourceType, sourceId) {
    try {
      const entriesRef = collection(db, 'journalEntries');
      const q = query(
        entriesRef, 
        where('companyId', '==', companyId),
        where('sourceType', '==', sourceType),
        where('sourceId', '==', sourceId),
        where('status', 'in', ['posted', 'draft'])
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error checking for existing journal entries:', error);
      return [];
    }
  },

  async validateManualEntryAllowed(companyId, expenseData) {
    // Check if this expense relates to a shipment that already has automatic journal entries
    if (expenseData.shipmentId) {
      const existingEntries = await this.checkForExistingJournalEntry(
        companyId, 
        'shipment_costing', 
        expenseData.shipmentId
      );
      
      if (existingEntries.length > 0) {
        throw new Error(
          `Cannot create manual expense: Shipment ${expenseData.shipmentId} already has automatic journal entries. ` +
          `Please use the Shipment Costing tab to modify costs, or reverse the existing journal entries first.`
        );
      }
    }
    
    return true;
  },

  // Function to clean up duplicate journal entries
  async cleanupDuplicateJournalEntries(companyId, createdBy) {
    console.log('🔄 Cleaning up duplicate journal entries for company:', companyId);
    
    try {
      const allJournalEntries = await this.getJournalEntries(companyId);
      console.log(`📋 Found ${allJournalEntries.length} total journal entries`);
      
      // Group journal entries by source (invoice or payment)
      const invoiceEntries = allJournalEntries.filter(entry => entry.sourceType === 'invoice');
      const paymentEntries = allJournalEntries.filter(entry => entry.sourceType === 'payment');
      
      console.log(`📋 Found ${invoiceEntries.length} invoice journal entries`);
      console.log(`📋 Found ${paymentEntries.length} payment journal entries`);
      
      // Find and remove duplicate invoice entries
      const invoiceGroups = {};
      invoiceEntries.forEach(entry => {
        const key = entry.sourceId || entry.reference;
        if (!invoiceGroups[key]) {
          invoiceGroups[key] = [];
        }
        invoiceGroups[key].push(entry);
      });
      
      let deletedInvoiceEntries = 0;
      for (const [key, entries] of Object.entries(invoiceGroups)) {
        if (entries.length > 1) {
          console.log(`🔍 Found ${entries.length} duplicate entries for invoice ${key}`);
          
          // Keep the first entry (usually the correct one) and delete the rest
          const entriesToDelete = entries.slice(1);
          
          for (const entryToDelete of entriesToDelete) {
            try {
              await this.deleteJournalEntry(entryToDelete.id);
              console.log(`🗑️ Deleted duplicate journal entry: ${entryToDelete.id}`);
              deletedInvoiceEntries++;
            } catch (error) {
              console.error(`❌ Failed to delete journal entry ${entryToDelete.id}:`, error);
            }
          }
        }
      }
      
      // Find and remove duplicate payment entries
      const paymentGroups = {};
      paymentEntries.forEach(entry => {
        const key = entry.sourceId || entry.reference;
        if (!paymentGroups[key]) {
          paymentGroups[key] = [];
        }
        paymentGroups[key].push(entry);
      });
      
      let deletedPaymentEntries = 0;
      for (const [key, entries] of Object.entries(paymentGroups)) {
        if (entries.length > 1) {
          console.log(`🔍 Found ${entries.length} duplicate entries for payment ${key}`);
          
          // Keep the first entry and delete the rest
          const entriesToDelete = entries.slice(1);
          
          for (const entryToDelete of entriesToDelete) {
            try {
              await this.deleteJournalEntry(entryToDelete.id);
              console.log(`🗑️ Deleted duplicate journal entry: ${entryToDelete.id}`);
              deletedPaymentEntries++;
            } catch (error) {
              console.error(`❌ Failed to delete journal entry ${entryToDelete.id}:`, error);
            }
          }
        }
      }
      
      console.log(`✅ Cleanup completed: Deleted ${deletedInvoiceEntries} duplicate invoice entries and ${deletedPaymentEntries} duplicate payment entries`);
      
      return {
        deletedInvoiceEntries,
        deletedPaymentEntries,
        totalDeleted: deletedInvoiceEntries + deletedPaymentEntries
      };
      
    } catch (error) {
      console.error('❌ Error cleaning up duplicate journal entries:', error);
      throw error;
    }
  }
}; 